(ns dashboard.events.auth0
  (:require
   [clojure.string :as str]
   [cljs.core.async :refer [go]]
   [cljs.core.async.interop :refer-macros [<p!]]
   [cljs.reader :refer [read-string]]
   [day8.re-frame.tracing :refer-macros [fn-traced]]
   [re-frame.core :as rf]
   [re-promise.core]
   [dashboard.interceptors :refer [standard-interceptors]]
   [dashboard.config :refer [auth0-domain auth0-client-id auth0-audience]]
   ["@auth0/auth0-spa-js" :refer [createAuth0Client]]))

(declare ^:private client)

(defn ^:private create-auth0-client!
  "Return a promise that resolves to the auth0 client instance."
  []
  ;; These values come from the Auth0 management console.
  ;; Create an application (of type SPA) then copy the values here.
  ;; They're not private so no need to keep them out of the repo.
  (let [config (clj->js
                {:domain auth0-domain
                 :client_id auth0-client-id
                 :audience auth0-audience})]
    (js/console.log (str "Creating auth0 client form domain " auth0-domain))
    (createAuth0Client config)))

(defn ^:private should-handle-callback?
  []
  (let [query-string js/window.location.search]
    (and (string? query-string)
         (.includes query-string "code=")
         (.includes query-string "state="))))

(defn ^:private handle-error [err stage]
  (let [promise-error? (= :promise-error (some-> err ex-data :error))
        err (if promise-error?
              (ex-cause err)
              err)
        message (.-message err)]
    (js/console.error err)
    (rf/dispatch-sync [::set-error-stage stage])
    (rf/dispatch-sync [::set-error-message message])))

(rf/reg-event-db
 ::set-error-stage
 standard-interceptors
 (fn [db [error-stage]]
   (assoc db :auth0/error-stage error-stage)))

(rf/reg-event-db
 ::set-error-message
 standard-interceptors
 (fn [db [error-message]]
   (assoc db :auth0/error-message error-message)))

(rf/reg-event-db
 ::set-user-data
 standard-interceptors
 (fn [db [user-data]]
   (assoc db :auth0/user-data (js->clj user-data))))

(rf/reg-event-db
 ::set-user-id-token
 standard-interceptors
 (fn [db [user-id-token]]
   (assoc db :auth0/user-id-token (aget user-id-token "__raw"))))

(rf/reg-event-db
 ::set-user-access-token
 standard-interceptors
 (fn [db [user-access-token]]
   (assoc db :auth0/user-access-token user-access-token)))

(rf/reg-event-db
 ::finish-loading
 standard-interceptors
 (fn [db _]
   (assoc db :auth0/loading? false)))

(rf/reg-sub
 :auth0/error-stage
 (fn [db _]
   (:auth0/error-stage db)))

(rf/reg-sub
 :auth0/error-message
 (fn [db _]
   (:auth0/error-message db)))

(rf/reg-sub
 :auth0/user-data
 (fn [db _]
   (:auth0/user-data db)))

(rf/reg-sub
 :auth0/user-id-token
 (fn [db _]
   (:auth0/user-id-token db)))

(rf/reg-sub
 :auth0/user-acces-token
 (fn [db _]
   (:auth0/user-access-token db)))

(rf/reg-sub
 :auth0/loading?
 (fn [db _]
   (:auth0/loading? db)))

(rf/reg-event-fx
 ::handle-login-error
 (fn [{:keys [_]} [_ err]]
   {:handle-error [err :login]
    :dispatch [::finish-loading]}))

(rf/reg-fx
 :logout
 (fn []
   (let [options #js {:returnTo js/window.location.origin}]
     (try
       (.logout client options)
       (catch js/Error err (handle-error err :logout))))))

(rf/reg-fx
 :handle-error
 (fn [[err stage]]
   (handle-error err stage)))

;;;;
;;;; Public interface follows
;;;;

(defn do-auth!
  "Do everything needed to handle authentication with Auth0.

  When a non-nil value (which must be serializable with `pr-str`) is passed to
  the re-frame ::login event of this namespace, the given make-navigate-event
  function will be called with that value as its only argument. The function
  should return a re-frame event, which will be dispatched if it is not nil."
  []
  (go
    (let [stage (atom :create-client)]
      (try
        (defonce client (<p! (create-auth0-client!)))
        (let [callback-result (when (should-handle-callback?)
                                (reset! stage :handle-callback)
                                (<p! (.handleRedirectCallback client)))
              page (some-> callback-result .-appState read-string)
              _ (js/window.history.replaceState #js {}
                                                js/document.title
                                                js/window.location.pathname)
              _ (reset! stage :check-authentication)
              authenticated? (<p! (.isAuthenticated client))]
          (if authenticated?
            (let [user-data (<p! (.getUser client))
                  user-id-token (<p! (.getIdTokenClaims client))
                  user-access-token (<p! (.getTokenSilently client))]
              (reset! stage :get-user-data)
              (rf/dispatch-sync [::set-user-data user-data])
              (rf/dispatch-sync [::set-user-access-token user-access-token])
              (rf/dispatch-sync [::set-user-id-token user-id-token]))
            (js/console.error "Not authenticated")))
        (catch js/Error err (handle-error err @stage))
        (finally
          (rf/dispatch [::finish-loading]))))))

(rf/reg-event-fx
 ::do-auth
 (fn [{:keys [db local-store-settings]} _]
   (do-auth!)
   {:db (assoc db :auth0/loading? true)}))

(rf/reg-event-fx
 ::login
 ;; page-after-login:
 ;; - represents the page to redirect to after login
 ;; - is passed to the fn you give to the do-auth! fn to create a re-frame
 ;;   navigate event that connects to your SPA routing/navigation system
 ;; - should be serializable with pr-str (a keyword works)
 ;; - can be nil/unspecified, in which case it represents the root path
 (fn-traced [_ [_ page-after-login]]
   (let [options #js {:redirect_uri js/window.location.origin
                      :appState (pr-str page-after-login)}]
     {:promise {:call #(when (some? client)
                         (.loginWithRedirect client options))
                :on-failure [::handle-login-error]}})))

(rf/reg-event-fx
 ::accept-invitation
 ;; the link sent in the email invitation that an end-user receives will be:
 ;; https://myapp.com/login?invitation={invitation_ticket_id}&organization={organization_id}&organization_name={organization_name}
 ;; Thus, the route in your application must accept invitation and organization
 ;; parameters through the query string. To start the invitation acceptance transaction,
 ;; it should forward both parameters along with the end-user to your Auth0 /authorize endpoint.
 ;; https://auth0.com/docs/authenticate/login/auth0-universal-login/configure-default-login-routes#invite-organization-members
 ;; Here is the reference of Auth0 SPA Js (the newer version of Auth0.js)
 ;; https://github.com/auth0/auth0-spa-js/blob/master/EXAMPLES.md#accept-user-invitations
 (fn-traced [_ [_ invitation organization]]
   (let [options #js {:invitation invitation
                      :organization organization
                      :redirect_uri js/window.location.origin}]
     {:promise {:call #(.loginWithRedirect client options)
                :on-failure [::handle-login-error]}})))

(rf/reg-event-fx
 ::logout
   (fn-traced [_ _]
     (let [options #js {:returnTo js/window.location.origin}]
       {:logout nil})))
